import { subject } from "@casl/ability";

import { ORDER_ASC } from "api/helpers/pagination";
import { portalApi, TAGS } from "api/rtkApi";
import { SUBJECT_CLIENT } from "models/role/subject";
import {
  PaginationParams,
  preparePaginationParams,
} from "utils/preparePaginationParams";

import type { OutstandingNotifications } from "models/Notification";
import type {
  NotificationSettings,
  NotificationSettingsUpdate,
} from "models/NotificationSettings";
import type { NotificationTestResponse } from "models/NotificationTestResponse";
import type { RTKMeta } from "models/RTKMeta";
import type { UUID } from "utils";
import type { ObjectWithId } from "utils/ObjectWithId";
import type { OrderableColumns } from "../constants/Application";
import type { AisRedirectUrls, Client } from "../models/Client";

type GetClientsResponse = {
  clients: Client[];
  totalItems: number;
};

type GetClientsQuery = {
  customerId?: UUID | UUID[];
} & PaginationParams<OrderableColumns>;

type PostCreateClientQueryResponse = {
  id: UUID;
  secret: UUID;
};

type PostUpdateClientQuery = {
  id: UUID;
  client: PostCreateClientQuery;
};

type PutUpdateClientStatusQuery = ObjectWithId & {
  client: Pick<Client, "active">;
};

type PostNotificationSettingsResponse = PostCreateClientQueryResponse;

type PostNotificationSettingsQuery = ObjectWithId & {
  settings: NotificationSettingsUpdate;
};

type GetOutstandingNotificationsResponse = OutstandingNotifications;

type PostTestNotificationResponse = NotificationTestResponse;

export type PostCreateClientQuery = {
  aisRedirectUrls?: AisRedirectUrls;
  customer: UUID;
  name: string;
  paymentFailureUrl?: string;
  paymentPendingUrl?: string;
  paymentSuccessUrl?: string;
  paymentCancelUrl?: string;
};

export type GetClientsRequest = {
  search: string;
  customerId?: UUID | UUID[];
};

export type GetClientsForAutocompleteResponse = {
  clients: Client[];
  totalItems: number;
};

export type GetClientsForAutocompleteQuery = {
  customerId?: UUID;
  search?: string;
};

export const CLIENTS_AUTOCOMPLETE_RESULTS_LIMIT = 100;

const extendedApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    createClient: build.mutation<
      PostCreateClientQueryResponse,
      PostCreateClientQuery
    >({
      query: (body) => ({
        url: "/clients",
        method: "POST",
        body,
      }),
      invalidatesTags: [TAGS.CLIENTS],
    }),
    getClient: build.query<Client, UUID>({
      query: (id) => ({
        url: `/clients/${id}`,
      }),
      transformResponse: (client: Client) => subject(SUBJECT_CLIENT, client),
      providesTags: (_, __, id) => [{ type: TAGS.CLIENT, id }],
    }),
    getClients: build.query<GetClientsResponse, GetClientsQuery>({
      query: ({ customerId, ...rest }) => ({
        url: "clients",
        params: {
          customerId,
          ...preparePaginationParams(rest),
        },
      }),
      providesTags: [{ type: TAGS.CLIENTS }],
      transformResponse: (clients: Client[], meta: RTKMeta) => ({
        clients: clients.map((client) => subject(SUBJECT_CLIENT, client)),
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    getClientsForAutocomplete: build.query<
      GetClientsForAutocompleteResponse,
      GetClientsForAutocompleteQuery
    >({
      query: ({ customerId, search }) => ({
        url: "clients",
        params: {
          "customerId[]": customerId,
          ...preparePaginationParams({
            search,
            sortBy: "name",
            order: ORDER_ASC,
            page: 1,
            limit: CLIENTS_AUTOCOMPLETE_RESULTS_LIMIT,
          }),
        },
      }),
      providesTags: [{ type: TAGS.CLIENTS_AUTOCOMPLETE }],
      transformResponse: (clients: Client[], meta: RTKMeta) => ({
        clients: clients,
        totalItems: Number(meta.response.headers.get("total-items")),
      }),
    }),
    regenerateSecret: build.mutation<{ secret: string }, UUID>({
      query: (id) => ({
        url: `/clients/${id}/secret/regenerate`,
        method: "POST",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { secret },
          } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              "getClient",
              id,
              (client: Client) => {
                client.secret = secret;
              }
            )
          );
        } catch {}
      },
    }),
    regenerateSignatureKey: build.mutation<Pick<Client, "signingKey">, UUID>({
      query: (id) => ({
        url: `/clients/${id}/signing-key`,
        method: "PATCH",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { signingKey },
          } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              "getClient",
              id,
              (client: Client) => {
                client.signingKey = signingKey;
              }
            )
          );
        } catch {}
      },
    }),
    updateClient: build.mutation<Client, PostUpdateClientQuery>({
      query: ({ id, client }) => ({
        url: `/clients/${id}`,
        method: "PUT",
        body: client,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: TAGS.CLIENT, id }],
    }),
    updateClientStatus: build.mutation<Client, PutUpdateClientStatusQuery>({
      query: ({ id, client }) => ({
        url: `/clients/${id}`,
        method: "PUT",
        body: client,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAGS.CLIENT, id },
        TAGS.CLIENTS,
      ],
    }),
    deleteClient: build.mutation<ObjectWithId, ObjectWithId>({
      query: ({ id }) => ({
        url: `/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { id }) => [TAGS.CLIENTS],
    }),
    getClientNotificationSettings: build.query<NotificationSettings, UUID>({
      query: (id) => ({
        url: `/clients/${id}/notifications/settings`,
      }),
      transformResponse: (settings: NotificationSettings) =>
        subject(SUBJECT_CLIENT, settings),
      providesTags: (_, __, id) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
    }),
    postNotificationSettings: build.mutation<
      PostNotificationSettingsResponse,
      PostNotificationSettingsQuery
    >({
      query: ({ id, settings }) => ({
        url: `/clients/${id}/notifications/settings`,
        method: "POST",
        body: settings,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
    }),
    postVerifyNotificationSettings: build.mutation<
      PostNotificationSettingsResponse,
      PostNotificationSettingsQuery
    >({
      query: ({ id, settings }) => ({
        url: `/verification/applications/${id}/settings`,
        method: "POST",
        body: settings,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
      extraOptions: {
        service: "notifications",
      },
    }),
    getVerifyNotificationSettings: build.query<NotificationSettings, UUID>({
      query: (id) => ({
        url: `/verification/applications/${id}/settings`,
      }),
      transformResponse: (settings: NotificationSettings) =>
        subject(SUBJECT_CLIENT, settings),
      providesTags: (_, __, id) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
      extraOptions: {
        service: "notifications",
      },
    }),
    getClientNotificationOutstanding: build.query<
      GetOutstandingNotificationsResponse,
      UUID
    >({
      query: (id) => ({
        url: `/payments/clients/${id}/outstanding`,
      }),
      transformResponse: (outstanding: OutstandingNotifications) =>
        subject(SUBJECT_CLIENT, outstanding),
      providesTags: (_, __, id) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
      extraOptions: {
        service: "notifications",
      },
    }),
    postTestNotification: build.mutation<PostTestNotificationResponse, UUID>({
      query: (id) => ({
        url: `/clients/${id}/notifications/test`,
        method: "POST",
      }),
      invalidatesTags: (_, __, id) => [
        { type: TAGS.CLIENT_NOTIFICATION_SETTINGS, id },
      ],
    }),
    regenerateNotificationSecret: build.mutation<{ secret: string }, UUID>({
      query: (id) => ({
        url: `/clients/${id}/notifications/settings/secret/regenerate`,
        method: "POST",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { secret },
          } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              "getClientNotificationSettings",
              id,
              (settings: NotificationSettings) => {
                settings.secret = secret;
              }
            )
          );
        } catch {}
      },
    }),
    switchCashManagementToggle: build.mutation<ObjectWithId, UUID>({
      query: (id) => ({
        url: `/clients/${id}/notifications/settings/payment-receival/toggle`,
        method: "PUT",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "getClientNotificationSettings",
              id,
              (settings: NotificationSettings) => {
                settings.paymentReceivalEnabled = !settings.paymentReceivalEnabled;
              }
            )
          );
        } catch {}
      },
    }),
  }),
});

export const {
  useCreateClientMutation,
  useGetClientQuery,
  useGetClientsQuery,
  useGetClientsForAutocompleteQuery,
  useGetClientNotificationSettingsQuery,
  usePostNotificationSettingsMutation,
  usePostVerifyNotificationSettingsMutation,
  useGetVerifyNotificationSettingsQuery,
  useGetClientNotificationOutstandingQuery,
  usePostTestNotificationMutation,
  useUpdateClientMutation,
  useUpdateClientStatusMutation,
  useDeleteClientMutation,
  useRegenerateSecretMutation,
  useRegenerateNotificationSecretMutation,
  useSwitchCashManagementToggleMutation,
  useRegenerateSignatureKeyMutation,
  util: clientsApiUtil,
} = extendedApi;
